/**
 * Generic admin CRUD page builder. Renders a list table + create/edit dialog form.
 */
import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";

export default function AdminCrudPage({
  title, endpoint, columns, fields, initial, transformIn = (x) => x, transformOut = (x) => x,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initial);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/admin/${endpoint}`); setItems(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startCreate = () => { setEditing(null); setForm(initial); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm(transformIn({ ...initial, ...it })); setOpen(true); };

  const save = async () => {
    try {
      const payload = transformOut(form);
      if (editing) await api.put(`/admin/${endpoint}/${editing.id}`, payload);
      else await api.post(`/admin/${endpoint}`, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[columns[0].key] || it.id}"?`)) return;
    try { await api.delete(`/admin/${endpoint}/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="heading-xl text-3xl">{title}</h1>
        <button onClick={startCreate} className="btn-primary text-sm" data-testid={`admin-${endpoint}-new`}>
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest">{c.label}</th>)}
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              : items.length === 0 ? <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-500">No items yet. Click "New" to create one.</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`row-${it.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 align-top">
                      {c.render ? c.render(it) : (it[c.key] ?? "—").toString().slice(0, 80)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(it)} className="p-1.5 hover:bg-gray-100" aria-label="Edit" data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(it)} className="p-1.5 hover:bg-red-50 text-red-600 ml-1" aria-label="Delete" data-testid={`delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-${endpoint}`}>
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {fields.map((f) => (
              <Field key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-outline text-sm" data-testid="dialog-cancel"><X className="w-4 h-4" /> Cancel</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="dialog-save"><Save className="w-4 h-4" /> Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ field, value, onChange }) {
  const label = <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{field.label}</label>;
  const cls = "mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm";

  if (field.type === "image")
    return <FileUpload value={value} onChange={onChange} label={field.label} accept="image/*" kind="image" />;
  if (field.type === "audio")
    return <FileUpload value={value} onChange={onChange} label={field.label} accept="audio/*,.mp3,.m4a,.wav,.ogg" kind="audio" />;
  if (field.type === "video")
    return <FileUpload value={value} onChange={onChange} label={field.label} accept="video/*,.mp4,.mov,.webm" kind="video" />;
  if (field.type === "file")
    return <FileUpload value={value} onChange={onChange} label={field.label} accept={field.accept || "*"} kind="file" />;
  if (field.type === "textarea")
    return <div>{label}<textarea rows={field.rows || 4} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`} /></div>;
  if (field.type === "richtext")
    return <RichText label={label} value={value} onChange={onChange} name={field.name} />;
  if (field.type === "number")
    return <div>{label}<input type="number" className={cls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} data-testid={`field-${field.name}`} /></div>;
  if (field.type === "boolean")
    return <div className="flex items-center gap-2"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${field.name}`} /><span className="text-sm font-bold">{field.label}</span></div>;
  if (field.type === "select")
    return <div>{label}<select className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`}>{field.options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
  if (field.type === "tags") {
    const val = Array.isArray(value) ? value.join(", ") : "";
    return <div>{label}<input className={cls} placeholder="Comma separated" value={val} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={`field-${field.name}`} /></div>;
  }
  if (field.type === "keyvalue") {
    const v = value && typeof value === "object" ? value : {};
    const lines = Object.entries(v).map(([k, val]) => `${k}: ${val}`).join("\n");
    return (
      <div>{label}
        <textarea rows={6}
          placeholder="One per line — Key: Value"
          className={cls + " font-mono text-xs"}
          defaultValue={lines}
          onBlur={(e) => {
            const out = {};
            e.target.value.split("\n").forEach((l) => {
              const idx = l.indexOf(":");
              if (idx > 0) out[l.slice(0, idx).trim()] = l.slice(idx + 1).trim();
            });
            onChange(out);
          }}
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  return <div>{label}<input className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.name}`} /></div>;
}

/**
 * RichText — lightweight textarea + toolbar that wraps the selected text
 * in HTML tags for B / I / U / H2 / colour / size. No 3rd-party editor needed.
 * Output is plain HTML stored in the same field, rendered with <div dangerouslySetInnerHTML> on the public site.
 */

function RichText({ label, value, onChange, name }) {
  const ref = useRef(null);

  const wrap = (open, close = "") => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const text = ta.value;
    const before = text.slice(0, start);
    const sel = text.slice(start, end) || "text";
    const after = text.slice(end);
    const next = `${before}${open}${sel}${close || open.replace("<", "</")}${after}`;
    onChange(next);
    // restore focus + select inserted content
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + open.length, start + open.length + sel.length);
    }, 10);
  };

  const insert = (snippet) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const text = ta.value;
    onChange(text.slice(0, start) + snippet + text.slice(start));
  };

  const setColor = (color) => wrap(`<span style="color:${color}">`, "</span>");
  const setSize = (size) => wrap(`<span style="font-size:${size}">`, "</span>");

  const Btn = ({ onClick, title, children }) => (
    <button type="button" onClick={onClick} title={title}
      className="px-2.5 py-1.5 text-xs font-bold border-r border-gray-300 hover:bg-[#FFCC00]/40 transition-colors"
    >{children}</button>
  );

  return (
    <div>
      {label}
      <div className="mt-2 flex flex-wrap items-center bg-gray-50 border border-gray-300 border-b-0" data-testid={`richtext-toolbar-${name}`}>
        <Btn onClick={() => wrap("<strong>")} title="Bold"><b>B</b></Btn>
        <Btn onClick={() => wrap("<em>")} title="Italic"><i>I</i></Btn>
        <Btn onClick={() => wrap("<u>")} title="Underline"><u>U</u></Btn>
        <Btn onClick={() => wrap("<h2>")} title="Heading">H2</Btn>
        <Btn onClick={() => wrap("<h3>")} title="Sub-heading">H3</Btn>
        <Btn onClick={() => wrap("<p>")} title="Paragraph">P</Btn>
        <Btn onClick={() => insert("\n<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>\n")} title="Bullet list">• List</Btn>
        <Btn onClick={() => setSize("0.85em")} title="Small text">A-</Btn>
        <Btn onClick={() => setSize("1.25em")} title="Large text">A+</Btn>
        <span className="px-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold">Color:</span>
        <button type="button" onClick={() => setColor("#E65C00")} className="w-7 h-7 bg-[#E65C00] border-r border-gray-300" title="Saffron" />
        <button type="button" onClick={() => setColor("#FFCC00")} className="w-7 h-7 bg-[#FFCC00] border-r border-gray-300" title="Yellow" />
        <button type="button" onClick={() => setColor("#1A1A1A")} className="w-7 h-7 bg-[#1A1A1A] border-r border-gray-300" title="Black" />
        <button type="button" onClick={() => setColor("#666666")} className="w-7 h-7 bg-gray-500 border-r border-gray-300" title="Grey" />
      </div>
      <textarea
        ref={ref}
        rows={10}
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-xs font-mono"
        placeholder="HTML allowed. Use the toolbar to add formatting."
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${name}`}
      />
      {value && (
        <details className="mt-2">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-[#E65C00]">Live preview</summary>
          <div className="mt-2 p-4 bg-white border border-gray-200 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: value }} />
        </details>
      )}
    </div>
  );
}

